import type {
  CampaignCreationCapabilities,
  RiskPreferenceV1,
  SmartCreationDraft,
  SmartCreationObjectiveRecommendation,
} from "@adpilot/shared";

export type RecommendationField = "targetAcos" | "totalDailyBudget";

export function minimumTotalDailyBudget(
  draft: SmartCreationDraft,
  capabilities: CampaignCreationCapabilities,
): number {
  const groups = new Set(draft.products.map((product) => product.variationGroupKey)).size;
  return (
    capabilities.dailyBudget.min *
    (draft.productOrganization === "campaign" ? Math.max(1, groups) : 1)
  );
}

export function formatCurrency(value: number, currencyCode: string, decimals: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch {
    return `${currencyCode} ${value.toFixed(decimals)}`;
  }
}

export function currencySymbol(currencyCode: string): string {
  try {
    return (
      new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0,
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? currencyCode
    );
  } catch {
    return currencyCode;
  }
}

export function personalityForDraft(draft: SmartCreationDraft): number {
  return (
    draft.aiPersonality ??
    (draft.riskPreference === "conservative" ? 1 : draft.riskPreference === "aggressive" ? 5 : 3)
  );
}

export function riskForPersonality(value: number): RiskPreferenceV1 {
  if (value <= 2) return "conservative";
  if (value >= 4) return "aggressive";
  return "balanced";
}

export function recommendationMatchesDraft(
  draft: SmartCreationDraft,
  recommendation: SmartCreationObjectiveRecommendation | null,
): boolean {
  if (!recommendation) return false;
  const recommendationPersonality =
    recommendation.aiPersonality ??
    (recommendation.riskPreference === "conservative"
      ? 1
      : recommendation.riskPreference === "aggressive"
        ? 5
        : 3);
  return (
    recommendation.applicability === "current" &&
    Date.parse(recommendation.expiresAt) > Date.now() &&
    recommendation.objective === draft.objective &&
    recommendationPersonality === personalityForDraft(draft) &&
    recommendation.promotionDateRange?.startDate === draft.promotionDateRange?.startDate &&
    recommendation.promotionDateRange?.endDate === draft.promotionDateRange?.endDate
  );
}
